"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BusSystemError = exports.isSystemErrorReply = exports.formatBusErrorDetails = exports.buildValidationErrorDetails = exports.SYSTEM_ERROR_CODES = exports.AckReplyV1 = void 0;
const zod_1 = require("zod");
const error_1 = require("./error");
/**
 * Универсальный ack-reply для команд, у которых reply — это голый `ok`-флаг
 * без полезной нагрузки. Используется в `defineCommand(_, _, AckReplyV1)` как
 * replySchema.
 *
 * Для команд с полезным reply (например `TrunkMutationReplyV1` возвращает `trunkId`)
 * или со специальными кодами ошибок — ДЕЛАТЬ ОТДЕЛЬНЫЕ discriminated-union
 * schemas в `replies.ts` соответствующего domain-пакета. AckReplyV1 строго для
 * случаев «успех/неуспех без деталей».
 *
 * Discriminated union по полю `isOk` даёт exhaustive `switch (reply.isOk)` на
 * стороне callsite — компилятор подсветит непокрытые ветки.
 */
exports.AckReplyV1 = zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({ isOk: zod_1.z.literal(true) }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        error: zod_1.z.string(),
        details: zod_1.z.unknown().optional(),
    }),
]);
/**
 * Коды system-ошибок, которыми `bus.respond` сам отвечает при `invalid_json`
 * (битый JSON), `validation_error` (input не прошёл `route.inputSchema.safeParse`),
 * `handler_error` (handler throw'нул).
 *
 * Эти коды НЕ описываются в domain replySchema — `bus.request` отсекает их до
 * `replySchema.parse` через throw. Цель: callsite не повторяет switch на этих
 * трёх case'ах в каждом месте; system-ошибки всплывают в глобальный error
 * handler как infrastructure exceptions (notify / log / Sentry), а не как
 * часть business contract'а.
 */
exports.SYSTEM_ERROR_CODES = ['handler_error', 'validation_error', 'invalid_json'];
/**
 * Построить `ValidationErrorDetails` из `ZodError`. Единая точка — используется
 * во всех точках валидации `bus.respond` / `bus.subscribe` / `bus.consumeJs`.
 */
const buildValidationErrorDetails = (err) => {
    const issues = err.issues.map((i) => ({
        path: i.path.join('.'),
        message: i.message,
        code: i.code,
    }));
    const message = issues.map((i) => (i.path ? `${i.path}: ${i.message}` : i.message)).join('; ');
    return { message, issues };
};
exports.buildValidationErrorDetails = buildValidationErrorDetails;
/**
 * Привести `details` любого system-error reply'я к человекочитаемой строке.
 * Единая точка форматирования (используется в `BusSystemError.message`, на
 * фронте в legacy `rawRequest`, и т.п.):
 *  - `string` → как есть;
 *  - объект с `issues[]` (`ValidationErrorDetails`) → многострочный список `• path: message`;
 *  - объект с `.message: string` → `.message`;
 *  - иначе → `JSON.stringify` (или `String`).
 */
const formatBusErrorDetails = (details) => {
    if (details === null || details === undefined)
        return undefined;
    if (typeof details === 'string')
        return details || undefined;
    if (typeof details === 'object') {
        const d = details;
        if (Array.isArray(d.issues)) {
            const lines = d.issues
                .map((i) => (i.path ? `• ${i.path}: ${i.message}` : `• ${i.message}`))
                .join('\n');
            if (lines)
                return lines;
        }
        if (typeof d.message === 'string' && d.message)
            return d.message;
        try {
            return JSON.stringify(details);
        }
        catch {
            return String(details);
        }
    }
    return String(details);
};
exports.formatBusErrorDetails = formatBusErrorDetails;
/**
 * Человекочитаемое сообщение system-error по коду — без технического префикса
 * `bus error on <subject>`. Это и есть `.message` у `BusSystemError`, чтобы
 * любой потребитель `err.message` (глобальный error handler, `catch` в форме)
 * показывал понятный текст автоматически.
 */
const userFacingBusMessage = (code, details) => {
    const formatted = (0, exports.formatBusErrorDetails)(details);
    switch (code) {
        case 'validation_error':
            return formatted ? `Проверьте поля:\n${formatted}` : 'Ошибка валидации данных';
        case 'invalid_json':
            return 'Некорректный формат запроса';
        case 'handler_error':
            return formatted ?? 'Внутренняя ошибка сервера';
        default:
            return formatted ?? 'Ошибка запроса';
    }
};
/**
 * Type guard: проверить decoded reply на system-error (`{isOk:false, error:<system code>}`).
 * Используется в `bus.request` перед `replySchema.parse` — если true, bus throw'ает
 * `BusSystemError` и не отдаёт payload caller'у.
 */
const isSystemErrorReply = (decoded) => {
    if (decoded === null || typeof decoded !== 'object')
        return false;
    const r = decoded;
    if (r.isOk !== false || typeof r.error !== 'string')
        return false;
    return exports.SYSTEM_ERROR_CODES.includes(r.error);
};
exports.isSystemErrorReply = isSystemErrorReply;
/**
 * Exception которое `bus.request` бросает при system-error reply'е. Сохраняет
 * `code` (`handler_error`/...) и `details` отдельно — UI может различать
 * формат уведомления, мониторинг — кодифицировать метрику.
 *
 * `.message` — **user-facing и без технического префикса** (см.
 * `userFacingBusMessage`): любой потребитель `err.message` (глобальный error
 * handler, `catch` в форме) показывает понятный текст автоматически. Технический
 * контекст не теряется — `subject` / `code` / `details` доступны как поля
 * экземпляра (для `console.error` / Sentry). `details` теперь `unknown` —
 * для `validation_error` это `ValidationErrorDetails`.
 */
class BusSystemError extends error_1.OmniError {
    code;
    details;
    subject;
    constructor(code, details, subject) {
        super(userFacingBusMessage(code, details));
        this.code = code;
        this.details = details;
        this.subject = subject;
        this.name = 'BusSystemError';
    }
}
exports.BusSystemError = BusSystemError;
//# sourceMappingURL=ack-reply.js.map